// ---------------------------------------------------------------
// userService.js — Fichas de usuario y nombres de usuario.
//
// Colección `users/{uid}`      → email, username, role, createdAt
// Colección `usernames/{name}` → { uid, email }  (id = username en
//   minúsculas; lectura pública para poder iniciar sesión con
//   nombre de usuario: se resuelve username → email y se entra
//   con signInWithEmailAndPassword).
// ---------------------------------------------------------------
import {
  deleteDoc,
  doc,
  getDoc,
  getDocFromServer,
  serverTimestamp,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

export const USERNAME_REGEX = /^[a-z0-9][a-z0-9._-]{2,19}$/;

export function normalizeUsername(raw) {
  return (raw || "").trim().toLowerCase();
}

/** ¿Está libre este username? (o pertenece ya a este uid) */
export async function isUsernameAvailable(username, uid = null) {
  const snap = await getDoc(doc(db, "usernames", username));
  return !snap.exists() || (uid !== null && snap.data().uid === uid);
}

/** Resuelve un username a su email (para el login). null si no existe. */
export async function resolveUsername(username) {
  const snap = await getDoc(doc(db, "usernames", normalizeUsername(username)));
  if (!snap.exists()) return null;
  const email = snap.data()?.email?.trim().toLowerCase();
  return email || null;
}

async function generateAvailableUsername(user, uid) {
  const base =
    normalizeUsername(user.displayName || user.email?.split("@")[0] || "usuario")
      .replace(/[^a-z0-9._-]/g, "")
      .replace(/^[._-]+/, "")
      .slice(0, 16) || "usuario";

  let candidate = base.length >= 3 ? base : `${base}123`.slice(0, 8);
  for (let i = 0; i < 5; i++) {
    if (await isUsernameAvailable(candidate, uid)) return candidate;
    candidate = `${base}${Math.floor(100 + Math.random() * 900)}`;
  }
  return candidate;
}

/**
 * Crea la ficha del usuario si no existe (primer login con correo
 * creado en consola, o primer login con Google). Genera un username
 * a partir del correo/nombre y reserva uno libre.
 */
/** Marca la última vez que esta persona entró en la web. Se escribe
 *  sólo este campo, así las reglas pueden permitirlo sin abrir nada
 *  más de la ficha. */
export function touchLastSeen(uid) {
  if (!uid) return Promise.resolve();
  return setDoc(
    doc(db, "users", uid),
    { lastSeen: serverTimestamp() },
    { merge: true }
  );
}

/**
 * Da de baja (o vuelve a dar de alta) a alguien SIN borrar su ficha.
 *
 * Borrar a una persona se lleva por delante su histórico de fichajes,
 * y ese registro hay que conservarlo cuatro años a disposición de la
 * Inspección (es lo que promete el consentimiento que firman). Por
 * eso quien deja la farmacia se desactiva: no entra, no aparece en
 * los cuadrantes ni en el reparto de tareas, pero todo lo suyo sigue
 * ahí y se puede consultar e imprimir.
 */
export function setUserActive(uid, activo) {
  // Ficha y directorio a la vez. Ahora "dar de baja" también le quita
  // el acceso: las reglas no dejan entrar a quien tiene activo: false.
  const batch = writeBatch(db);
  batch.set(
    doc(db, "users", uid),
    { activo: !!activo, desactivadoAt: activo ? null : serverTimestamp() },
    { merge: true }
  );
  batch.set(doc(db, "directorio", uid), { activo: !!activo }, { merge: true });
  return batch.commit();
}

/**
 * Comprueba la ficha de quien acaba de entrar.
 *
 * @returns "ok" si puede usar el portal, "sin-acceso" si no tiene ficha
 *   (ni invitación) o está dado de baja, "sin-conexion" si no se ha
 *   podido comprobar.
 *
 * Antes, a quien entraba sin ficha se le creaba una con rol "user".
 * Eso dejaba entrar a cualquiera con cuenta de Firebase: a un
 * ex-empleado eliminado (su cuenta de acceso no se puede borrar desde
 * el navegador) y a CUALQUIER persona con cuenta de Google, porque el
 * botón "Entrar con Google" crea la cuenta solo. Ahora la ficha sólo
 * se crea si la dirección dejó una invitación para ese correo; las
 * reglas de Firestore exigen lo mismo, así que no basta con saltarse
 * esta función.
 */
export async function ensureUserProfile(user) {
  const ref = doc(db, "users", user.uid);
  const email = user.email?.trim().toLowerCase();
  if (!email) return "sin-acceso";

  // OJO: esta lectura TIENE que venir del servidor.
  // Con la caché local activada, un `getDoc` normal responde desde la
  // caché cuando la red falla (WiFi de negocio, portal cautivo, cambio
  // de móvil a WiFi…). En un dispositivo con la caché vacía eso
  // devuelve "no existe" aunque la ficha esté ahí, y abajo se recreaba
  // desde cero: username sacado del correo y rol "user". Es decir, te
  // degradaba tu propia cuenta. Si no se puede confirmar contra el
  // servidor, no se toca nada.
  let snap;
  try {
    snap = await getDocFromServer(ref);
  } catch (err) {
    console.warn("Sin conexión para comprobar la ficha: no se toca.", err);
    return "sin-conexion";
  }

  if (!snap.exists()) {
    // Sin ficha: sólo se crea si hay una invitación para este correo
    let inv;
    try {
      inv = await getDocFromServer(doc(db, "invitaciones", email));
    } catch (err) {
      console.warn("No se pudo comprobar la invitación:", err);
      return "sin-conexion";
    }
    if (!inv.exists()) return "sin-acceso";

    const { username, role } = inv.data();
    const batch = writeBatch(db);
    // Sin `passwordPropia`: quien vuelve puede estar entrando con su
    // contraseña de antes, que la dirección conocía. Así le saldrá el
    // aviso de cambiarla.
    batch.set(ref, { email, username, role, createdAt: serverTimestamp() });
    batch.set(doc(db, "usernames", username), { uid: user.uid, email });
    batch.set(doc(db, "directorio", user.uid), { username, role });
    batch.delete(doc(db, "invitaciones", email));
    await batch.commit();
    return "ok";
  }

  // Dado de baja: conserva todo su histórico, pero no entra
  if (snap.data().activo === false) return "sin-acceso";

  /* ---- La ficha YA EXISTE ----
     A partir de aquí esta función NO PUEDE RENOMBRAR A NADIE. Antes,
     si por lo que fuera la ficha llegaba sin el campo `username`, se
     inventaba uno a partir del correo y lo escribía: te encontrabas
     llamándote como tu correo sin haber tocado nada, y encima el
     nombre viejo seguía funcionando para entrar (nadie borraba su
     reserva). Ha pasado dos veces.

     El nombre de una persona sólo se cambia desde Gestión de usuarios,
     que es donde se hace a conciencia y liberando el nombre anterior.
     Aquí, como mucho, se repara el mapeo nombre→correo si se ha
     quedado desincronizado, y se completa el correo si falta. */
  const data = snap.data();
  const username = data.username;

  if (!username) {
    // Sin nombre en la ficha: NO se inventa uno. Se avisa para que el
    // administrador lo arregle desde Usuarios. La pantalla mientras
    // tanto enseña el correo, que es feo pero no rompe nada ni deja
    // rastro en la base de datos.
    console.warn(
      "Tu ficha no tiene nombre de usuario. Que un administrador te lo " +
        "asigne desde Usuarios; no se genera uno automáticamente para no " +
        "cambiarte el nombre sin querer."
    );
    if (data.email !== email) {
      await setDoc(ref, { email }, { merge: true });
    }
    return "ok";
  }

  // ¿Está el mapeo nombre→{uid,email} al día? Si no lo está se repara,
  // pero SIEMPRE conservando el nombre que ya tiene la persona.
  const usernameRef = doc(db, "usernames", username);
  let usernameOk = false;
  try {
    const usernameSnap = await getDocFromServer(usernameRef);
    usernameOk =
      usernameSnap.exists() &&
      usernameSnap.data()?.uid === user.uid &&
      usernameSnap.data()?.email === email;
  } catch (err) {
    // Sin confirmación del servidor no se toca nada: una lectura desde
    // una caché fría diría "no existe" y reescribiríamos a ciegas.
    console.warn("Sin conexión para comprobar el nombre: no se toca.", err);
    return "ok";
  }

  const emailOk = data.email === email;
  if (usernameOk && emailOk) return "ok";

  const batch = writeBatch(db);
  if (!emailOk) {
    // Sólo el correo. Ni el nombre ni el rol se tocan desde aquí.
    batch.set(ref, { email, ...(data.createdAt ? {} : { createdAt: serverTimestamp() }) }, { merge: true });
  }
  if (!usernameOk) {
    batch.set(usernameRef, { uid: user.uid, email });
  }
  await batch.commit();
  return "ok";
}

/** Reserva `next` y libera `prev` al renombrar a un usuario */
export async function swapUsername(uid, email, prev, next) {
  const batch = writeBatch(db);
  if (prev && prev !== next) batch.delete(doc(db, "usernames", prev));
  batch.set(doc(db, "usernames", next), { uid, email });
  await batch.commit();
}

export function deleteUsernameDoc(username) {
  return username ? deleteDoc(doc(db, "usernames", username)) : Promise.resolve();
}

/**
 * Deja `directorio` igual que `users` (sólo nombre, rol y si sigue).
 *
 * El directorio nació vacío, y sin él el equipo no vería los nombres de
 * sus compañeros. Se rellena solo cada vez que entra un administrador,
 * escribiendo únicamente lo que no coincide, así que en el día a día no
 * gasta casi nada. Las altas, cambios y bajas ya lo mantienen al día;
 * esto es la red por si algo se quedó descolgado.
 */
export async function sincronizarDirectorio() {
  const { collection, getDocs } = await import("firebase/firestore");
  const [fichas, dir] = await Promise.all([
    getDocs(collection(db, "users")),
    getDocs(collection(db, "directorio")),
  ]);
  const actual = new Map(dir.docs.map((d) => [d.id, d.data()]));
  const batch = writeBatch(db);
  let cambios = 0;

  for (const f of fichas.docs) {
    const u = f.data();
    const debe = {
      username: u.username || "",
      displayName: u.displayName || "",
      role: u.role || "user",
      activo: u.activo !== false,
    };
    const hay = actual.get(f.id);
    const igual =
      hay &&
      hay.username === debe.username &&
      (hay.displayName || "") === debe.displayName &&
      hay.role === debe.role &&
      (hay.activo !== false) === debe.activo;
    if (!igual) {
      batch.set(doc(db, "directorio", f.id), debe);
      cambios += 1;
    }
    actual.delete(f.id);
  }
  // Lo que queda en el directorio sin ficha detrás: personas eliminadas
  for (const uid of actual.keys()) {
    batch.delete(doc(db, "directorio", uid));
    cambios += 1;
  }
  if (cambios) await batch.commit();
  return cambios;
}
